import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useRestaurant } from '../contexts/RestaurantContext';
import { Card, CardContent } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import {
  Users,
  TrendingUp,
  UserPlus,
  ArrowRight,
  Plus,
  Copy,
  Check,
  ChevronDown,
  ExternalLink,
  MailCheck,
  Shield,
  UserCheck,
  UserX,
  TabletSmartphone,
  LogIn,
} from 'lucide-react';
import apiClient from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const MODULES = [
  { key: 'dashboard', labelKey: 'organization.moduleDashboard' },
  { key: 'carlo_agent', labelKey: 'organization.moduleCarloAgent' },
  { key: 'events', labelKey: 'organization.moduleEvents' },
  { key: 'sales', labelKey: 'organization.moduleSales' },
  { key: 'finance', labelKey: 'organization.moduleFinance' },
  { key: 'products', labelKey: 'organization.moduleProducts' },
  { key: 'inventory', labelKey: 'organization.moduleInventory' },
  { key: 'orders', labelKey: 'organization.moduleOrders' },
  { key: 'employees', labelKey: 'organization.moduleEmployees' },
  { key: 'tasks', labelKey: 'organization.moduleTasks' },
];

const LEVELS = ['none', 'view', 'edit'];

function getApiClient() {
  const token = localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };
  const restaurantId = localStorage.getItem('current_restaurant_id');
  if (restaurantId) {
    headers['X-Restaurant-Id'] = restaurantId;
  }
  return {
    get: (url) => apiClient.get(`${API_URL}${url}`, { headers }).then((r) => r.data),
    post: (url, data) => apiClient.post(`${API_URL}${url}`, data, { headers }).then((r) => r.data),
    patch: (url, data) => apiClient.patch(`${API_URL}${url}`, data, { headers }).then((r) => r.data),
    delete: (url) => apiClient.delete(`${API_URL}${url}`, { headers }).then((r) => r.data),
  };
}

function formatOrgPanelDate(dateStr, locale = 'en-US') {
  if (!dateStr) return '';
  const date = new Date(`${dateStr}T00:00:00`);
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
  }).format(date);
}

function getDisplayName(member) {
  const fullName = [member.first_name, member.last_name].filter(Boolean).join(' ').trim();
  return fullName || member.username;
}

function getMemberInitials(member) {
  return (member.first_name?.[0] || member.username?.[0] || '?').toUpperCase();
}

function countEnabledModules(modulePermissions) {
  return Object.values(modulePermissions || {}).filter((level) => level && level !== 'none').length;
}

export default function OrgPanel() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, startImpersonation } = useAuth();
  const { switchRestaurant } = useRestaurant();

  const [newRestaurantName, setNewRestaurantName] = useState('');
  const [showAddRestaurant, setShowAddRestaurant] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('staff');
  const [inviteRestaurantId, setInviteRestaurantId] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [inviteNotice, setInviteNotice] = useState(null);
  const [copied, setCopied] = useState(false);
  const [expandedMembers, setExpandedMembers] = useState({});
  const [teamError, setTeamError] = useState('');
  const [signupRequestError, setSignupRequestError] = useState('');
  const [showSharedAccountModal, setShowSharedAccountModal] = useState(false);
  const [sharedAccountName, setSharedAccountName] = useState('Kitchen iPad');
  const [sharedRestaurantId, setSharedRestaurantId] = useState('');
  const [sharedEmployeeIds, setSharedEmployeeIds] = useState([]);
  const [createdSharedCredentials, setCreatedSharedCredentials] = useState(null);
  const [copiedSharedCredentials, setCopiedSharedCredentials] = useState(false);

  const api = getApiClient();

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['org-overview'],
    queryFn: () => api.get('/organizations/overview/'),
  });

  const { data: teamMembers = [], isLoading: teamLoading } = useQuery({
    queryKey: ['org-users'],
    queryFn: () => api.get('/auth/org-users/'),
    onError: () => setTeamError('Failed to load team members.'),
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees', 'org-panel'],
    queryFn: () => api.get('/employees/?scope=organization'),
    initialData: [],
  });

  const org = overview?.organization;
  const restaurants = overview?.restaurants || [];
  const canReviewSignupRequests = user?.is_superadmin || user?.email?.toLowerCase() === 'admin@carlo.de';

  const { data: signupRequests = [] } = useQuery({
    queryKey: ['signup-requests', 'pending'],
    queryFn: () => api.get('/auth/signup-requests/?status=pending'),
    enabled: canReviewSignupRequests,
    initialData: [],
  });

  const createRestaurant = useMutation({
    mutationFn: (name) => api.post('/organizations/restaurants/', { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-overview'] });
      setNewRestaurantName('');
      setShowAddRestaurant(false);
    },
  });

  const createInvite = useMutation({
    mutationFn: (data) => api.post('/auth/invites/', data),
    onSuccess: (data) => {
      setInviteLink(data.invite_url || `${window.location.origin}/register/invite/${data.token}`);
      setInviteNotice({
        recipient: data.email,
        sent: data.email_sent === true,
      });
      setInviteEmail('');
    },
  });

  const decideSignupRequest = useMutation({
    mutationFn: ({ id, decision }) => api.post(`/auth/signup-requests/${id}/${decision}/`, {}),
    onSuccess: (data) => {
      setSignupRequestError('');
      queryClient.invalidateQueries({ queryKey: ['signup-requests'] });
      queryClient.invalidateQueries({ queryKey: ['org-overview'] });
      if (data.invite_url) {
        setInviteLink(data.invite_url);
        setInviteNotice({
          recipient: data.email,
          sent: data.email_sent === true,
        });
      }
    },
    onError: (error) => {
      setSignupRequestError(error?.response?.data?.detail || 'Failed to update signup request.');
    },
  });

  const createSharedAccount = useMutation({
    mutationFn: (data) => api.post('/auth/org-users/shared-accounts/', data),
    onSuccess: (data) => {
      setTeamError('');
      setCreatedSharedCredentials(data.credentials);
      setSharedAccountName('Kitchen iPad');
      setSharedEmployeeIds([]);
      setShowSharedAccountModal(false);
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
      queryClient.invalidateQueries({ queryKey: ['org-overview'] });
    },
    onError: (error) => {
      setTeamError(
        error?.response?.data?.detail ||
        error?.response?.data?.account_name?.[0] ||
        'Failed to create shared account.',
      );
    },
  });

  const updateMember = useMutation({
    mutationFn: ({ id, data }) => api.patch(`/auth/org-users/${id}/`, data),
    onSuccess: () => {
      setTeamError('');
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
      queryClient.invalidateQueries({ queryKey: ['org-overview'] });
    },
    onError: (error) => {
      setTeamError(
        error?.response?.data?.detail ||
        error?.response?.data?.non_field_errors?.[0] ||
        'Failed to update team member.',
      );
    },
  });

  const deleteMember = useMutation({
    mutationFn: (id) => api.delete(`/auth/org-users/${id}/`),
    onSuccess: () => {
      setTeamError('');
      queryClient.invalidateQueries({ queryKey: ['org-users'] });
      queryClient.invalidateQueries({ queryKey: ['org-overview'] });
    },
    onError: (error) => {
      setTeamError(
        error?.response?.data?.detail ||
        'Failed to delete team member.',
      );
    },
  });

  const impersonateMember = useMutation({
    mutationFn: (id) => startImpersonation(id),
    onSuccess: () => {
      setTeamError('');
      navigate('/dashboard');
    },
    onError: (error) => {
      setTeamError(
        error?.response?.data?.detail ||
        'Failed to start debug login.',
      );
    },
  });

  const handleOpenRestaurant = (restaurantId) => {
    switchRestaurant(restaurantId);
    navigate('/dashboard');
  };

  const handleCreateInvite = (e) => {
    e.preventDefault();
    const restaurantId = inviteRestaurantId || overview?.restaurants?.[0]?.id;
    if (!restaurantId) return;
    setInviteLink('');
    setInviteNotice(null);
    createInvite.mutate({
      email: inviteEmail || null,
      role: inviteRole,
      restaurant_id: restaurantId,
    });
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const selectedSharedRestaurantId = sharedRestaurantId || restaurants[0]?.id || '';
  const sharedEmployeeOptions = selectedSharedRestaurantId
    ? employees.filter((employee) => String(employee.restaurant) === String(selectedSharedRestaurantId))
    : [];

  const handleSharedFormEmployeeToggle = (employeeId) => {
    setSharedEmployeeIds((current) => (
      current.includes(employeeId)
        ? current.filter((id) => id !== employeeId)
        : [...current, employeeId]
    ));
  };

  const handleSharedRestaurantChange = (restaurantId) => {
    setSharedRestaurantId(restaurantId);
    setSharedEmployeeIds([]);
  };

  const handleCreateSharedAccount = (e) => {
    e.preventDefault();
    if (!sharedAccountName.trim() || !selectedSharedRestaurantId) return;
    createSharedAccount.mutate({
      account_name: sharedAccountName.trim(),
      restaurant_id: Number(selectedSharedRestaurantId),
      shared_employee_ids: sharedEmployeeIds,
    });
  };

  const handleCopySharedCredentials = () => {
    if (!createdSharedCredentials) return;
    navigator.clipboard.writeText([
      `Login: ${createdSharedCredentials.login_url}`,
      `Username: ${createdSharedCredentials.username}`,
      `Password: ${createdSharedCredentials.password}`,
    ].join('\n'));
    setCopiedSharedCredentials(true);
    setTimeout(() => setCopiedSharedCredentials(false), 2000);
  };

  const saveMember = async (memberId, data) => {
    await updateMember.mutateAsync({ id: memberId, data });
  };

  const handleRoleChange = async (member, newRole) => {
    await saveMember(member.id, { role: newRole });
  };

  const handleToggleActive = async (member) => {
    await saveMember(member.id, { is_active: !member.is_active });
  };

  const handleDeleteMember = async (member) => {
    if (!window.confirm(`Delete ${getDisplayName(member)} permanently? This cannot be undone.`)) {
      return;
    }
    await deleteMember.mutateAsync(member.id);
  };

  const handleImpersonateMember = (member) => {
    impersonateMember.mutate(member.id);
  };

  const handlePermissionChange = async (member, moduleKey, level) => {
    await saveMember(member.id, {
      module_permissions: {
        [moduleKey]: level,
      },
    });
  };

  const handleBulkPermissionChange = async (member, level) => {
    const payload = Object.fromEntries(MODULES.map((module) => [module.key, level]));
    await saveMember(member.id, { module_permissions: payload });
  };

  const getSharedEmployeeOptions = (member) => {
    if (!member.restaurant) return employees;
    return employees.filter((employee) => employee.restaurant === member.restaurant);
  };

  const handleSharedToggle = async (member, enabled) => {
    await saveMember(member.id, {
      is_shared_account: enabled,
      shared_employee_ids: enabled ? (member.shared_employee_ids || []) : [],
    });
  };

  const handleSharedEmployeeToggle = async (member, employeeId) => {
    const current = new Set(member.shared_employee_ids || []);
    if (current.has(employeeId)) {
      current.delete(employeeId);
    } else {
      current.add(employeeId);
    }
    await saveMember(member.id, {
      is_shared_account: true,
      shared_employee_ids: Array.from(current),
    });
  };

  const memberCount = teamMembers.length;

  const roleLabel = (role) => {
    const labels = {
      org_admin: t('orgPanel.roleOrgAdmin'),
      restaurant_admin: t('orgPanel.roleRestaurantAdmin'),
      staff: t('orgPanel.roleStaff'),
    };
    return labels[role] || role;
  };

  const moduleLabel = (moduleKey) => {
    const item = MODULES.find((module) => module.key === moduleKey);
    return item ? t(item.labelKey) : moduleKey;
  };

  if (overviewLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <div className="mb-5 sm:mb-8">
          <h1 className="text-2xl font-bold text-slate-900">
            {org?.name || t('orgPanel.title')}
          </h1>
          <p className="text-slate-500 text-sm mt-1">{t('orgPanel.subtitle')}</p>
        </div>

        {canReviewSignupRequests && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-slate-800 mb-1">Signup requests</h2>
            <p className="text-sm text-slate-500 mb-4">New restaurants waiting for Phillip's approval.</p>

            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-5">
                {signupRequestError && (
                  <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {signupRequestError}
                  </div>
                )}
                {signupRequests.length === 0 ? (
                  <p className="text-sm text-slate-500">No pending signup requests.</p>
                ) : (
                  <div className="space-y-3">
                    {signupRequests.map((request) => {
                      const requesterName = [request.first_name, request.last_name].filter(Boolean).join(' ').trim();
                      return (
                        <div key={request.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-semibold text-slate-900">{request.restaurant_name}</p>
                                {request.organization_name && request.organization_name !== request.restaurant_name && (
                                  <span className="rounded-full bg-white px-2 py-0.5 text-xs text-slate-500 border border-slate-200">
                                    {request.organization_name}
                                  </span>
                                )}
                              </div>
                              <p className="mt-1 text-sm text-slate-600">
                                {requesterName ? `${requesterName} · ` : ''}{request.email}
                              </p>
                              {request.note && (
                                <p className="mt-2 text-sm text-slate-500">{request.note}</p>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-2 md:justify-end">
                              <Button
                                size="sm"
                                onClick={() => decideSignupRequest.mutate({ id: request.id, decision: 'accept' })}
                                disabled={decideSignupRequest.isPending}
                              >
                                <UserCheck className="w-4 h-4 mr-1.5" />
                                Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => decideSignupRequest.mutate({ id: request.id, decision: 'reject' })}
                                disabled={decideSignupRequest.isPending}
                              >
                                <UserX className="w-4 h-4 mr-1.5" />
                                Reject
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        <div className="mb-6 sm:mb-10">
          <h2 className="text-lg font-semibold text-slate-800 mb-3 sm:mb-4">{t('orgPanel.yourRestaurants')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {restaurants.map((r) => (
              <Card key={r.id} className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  {(() => {
                    const statsDateLabel = formatOrgPanelDate(
                      r.stats_date,
                      i18n.language === 'de' ? 'de-DE' : 'en-US',
                    );
                    const revenueLabel = r.stats_is_today ? t('orgPanel.todayRevenue') : t('orgPanel.latestRevenue');
                    const guestsLabel = r.stats_is_today ? t('orgPanel.todayGuests') : t('orgPanel.latestGuests');

                    return (
                      <>
                        <h3 className="font-semibold text-slate-900 text-base mb-3">{r.name}</h3>
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-500 flex items-center gap-1.5">
                              <TrendingUp className="w-3.5 h-3.5" />
                              {revenueLabel}
                            </span>
                            <span className="font-medium text-slate-900">
                              EUR {r.today_revenue?.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-500">{guestsLabel}</span>
                            <span className="font-medium text-slate-900">{r.today_guests}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-500 flex items-center gap-1.5">
                              <Users className="w-3.5 h-3.5" />
                              {t('orgPanel.staffCount')}
                            </span>
                            <span className="font-medium text-slate-900">{r.staff_count}</span>
                          </div>
                        </div>
                        {!r.stats_is_today && statsDateLabel && (
                          <p className="text-xs text-slate-400 mb-3">
                            {t('orgPanel.latestUpload', { date: statsDateLabel })}
                          </p>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => handleOpenRestaurant(r.id)}
                        >
                          {t('orgPanel.open')}
                          <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                        </Button>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            ))}

            {showAddRestaurant ? (
              <Card className="border border-dashed border-slate-300 shadow-sm">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-slate-700 text-sm mb-3">{t('orgPanel.addRestaurant')}</h3>
                  <form onSubmit={(e) => { e.preventDefault(); if (newRestaurantName.trim()) createRestaurant.mutate(newRestaurantName.trim()); }}>
                    <Input
                      value={newRestaurantName}
                      onChange={(e) => setNewRestaurantName(e.target.value)}
                      placeholder={t('orgPanel.restaurantNamePlaceholder')}
                      className="mb-3"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <Button type="submit" size="sm" disabled={!newRestaurantName.trim() || createRestaurant.isPending}>
                        {createRestaurant.isPending ? '...' : t('orgPanel.create')}
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setShowAddRestaurant(false)}>
                        {t('orgPanel.cancel')}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            ) : (
              <button
                onClick={() => setShowAddRestaurant(true)}
                className="border border-dashed border-slate-300 rounded-lg p-5 flex flex-col items-center justify-center gap-2 text-slate-400 hover:text-slate-600 hover:border-slate-400 transition-colors min-h-[160px]"
              >
                <Plus className="w-6 h-6" />
                <span className="text-sm font-medium">{t('orgPanel.addRestaurant')}</span>
              </button>
            )}
          </div>
        </div>

        <div className="mb-6 sm:mb-10">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-800 mb-1">{t('orgPanel.team')}</h2>
              <p className="text-sm text-slate-500">
                {t('orgPanel.teamCount', { count: memberCount, locations: restaurants.length })}
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={() => setShowSharedAccountModal(true)}
              className="self-start md:self-auto"
            >
              <UserPlus className="w-4 h-4 mr-1.5" />
              Create shared login
            </Button>
          </div>

          {teamError && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {teamError}
            </div>
          )}

          {createdSharedCredentials && (
            <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold text-emerald-900">Shared login created</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopySharedCredentials}
                  className="border-emerald-200 bg-white text-emerald-800 hover:bg-emerald-50"
                >
                  {copiedSharedCredentials ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  <span className="ml-1">{copiedSharedCredentials ? 'Copied' : 'Copy credentials'}</span>
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <p className="text-xs font-medium text-emerald-700">Login</p>
                  <p className="break-all text-emerald-950">{createdSharedCredentials.login_url}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-emerald-700">Username</p>
                  <p className="break-all font-semibold text-emerald-950">{createdSharedCredentials.username}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-emerald-700">Password</p>
                  <p className="break-all font-semibold text-emerald-950">{createdSharedCredentials.password}</p>
                </div>
              </div>
            </div>
          )}

          <Dialog open={showSharedAccountModal} onOpenChange={setShowSharedAccountModal}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create shared staff login</DialogTitle>
                <DialogDescription>
                  Enter the account details and choose who can use this device login.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleCreateSharedAccount} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Account name
                    </label>
                    <Input
                      value={sharedAccountName}
                      onChange={(e) => setSharedAccountName(e.target.value)}
                      placeholder="Kitchen iPad"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Restaurant
                    </label>
                    <div className="relative">
                      <select
                        value={selectedSharedRestaurantId}
                        onChange={(e) => handleSharedRestaurantChange(e.target.value)}
                        className="w-full appearance-none bg-white border border-slate-200 rounded-lg px-3 py-2 pr-8 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {restaurants.map((r) => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Employees</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {sharedEmployeeOptions.map((employee) => {
                      const selected = sharedEmployeeIds.includes(employee.id);
                      return (
                        <button
                          key={employee.id}
                          type="button"
                          onClick={() => handleSharedFormEmployeeToggle(employee.id)}
                          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                            selected
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                              : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-white'
                          }`}
                        >
                          <span className={`h-2 w-2 rounded-full ${selected ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                          <span className="min-w-0">
                            <span className="block truncate font-medium">{employee.name}</span>
                            <span className="block truncate text-xs opacity-70">{employee.role}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {selectedSharedRestaurantId && sharedEmployeeOptions.length === 0 && (
                    <p className="text-sm text-slate-500">No employees found for this restaurant yet.</p>
                  )}
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowSharedAccountModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={!sharedAccountName.trim() || !selectedSharedRestaurantId || createSharedAccount.isPending}
                  >
                    <UserPlus className="w-4 h-4 mr-1.5" />
                    {createSharedAccount.isPending ? 'Creating...' : 'Create login'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <div className="space-y-4">
            {teamLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600" />
              </div>
            ) : teamMembers.length === 0 ? (
              <Card className="border border-slate-200 shadow-sm">
                <CardContent className="p-6 text-center text-slate-400">
                  {t('orgPanel.noMembers')}
                </CardContent>
              </Card>
            ) : (
              teamMembers.map((member) => {
                const isSelf = member.id === user?.id;
                const isStaff = member.role === 'staff';
                const isAdminRole = member.role === 'org_admin' || member.role === 'restaurant_admin';
                const modulePermissions = member.module_permissions || {};
                const enabledModules = countEnabledModules(modulePermissions);
                const isExpanded = !!expandedMembers[member.id];
                const canImpersonate = user?.is_superadmin && member.is_active && !member.is_superadmin && !isSelf;

                return (
                  <Card key={member.id} className="border border-slate-200 shadow-sm">
                    <CardContent className="p-5">
                      <div className="flex flex-col gap-4">
                        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="w-11 h-11 rounded-full bg-slate-900 flex items-center justify-center flex-shrink-0">
                              <span className="text-white font-semibold text-sm">{getMemberInitials(member)}</span>
                            </div>
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-semibold text-slate-900 truncate">
                                  {getDisplayName(member)}
                                </p>
                                {isSelf && (
                                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                    {t('orgPanel.you')}
                                  </span>
                                )}
                                {!member.is_active && (
                                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                                    Inactive
                                  </span>
                                )}
                                {isAdminRole && (
                                  <span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                                    <Shield className="w-3 h-3" />
                                    Full access
                                  </span>
                                )}
                                {isStaff && (
                                  <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">
                                    {enabledModules} page{enabledModules === 1 ? '' : 's'}
                                  </span>
                                )}
                                {member.is_shared_account && (
                                  <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                                    <TabletSmartphone className="w-3 h-3" />
                                    Shared
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-slate-500 break-all">{member.email || '--'}</p>
                              <p className="text-xs text-slate-400 mt-0.5">
                                {(member.restaurants || [])
                                  .map((restaurant) => restaurant.name)
                                  .filter(Boolean)
                                  .join(', ') || '--'}
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                            {isSelf ? (
                              <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                                {roleLabel(member.role)}
                              </span>
                            ) : (
                              <div className="relative">
                                <select
                                  value={member.role}
                                  onChange={(e) => handleRoleChange(member, e.target.value)}
                                  disabled={updateMember.isPending}
                                  className="appearance-none bg-white border border-slate-200 rounded-lg px-3 py-2 pr-8 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60"
                                >
                                  <option value="staff">{t('orgPanel.roleStaff')}</option>
                                  <option value="restaurant_admin">{t('orgPanel.roleRestaurantAdmin')}</option>
                                  <option value="org_admin">{t('orgPanel.roleOrgAdmin')}</option>
                                </select>
                                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                              </div>
                            )}

                            {!isSelf && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleToggleActive(member)}
                                disabled={updateMember.isPending}
                                className={`text-xs ${
                                  member.is_active
                                    ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                                    : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                                }`}
                              >
                                {member.is_active ? (
                                  <>
                                    <UserCheck className="w-3.5 h-3.5 mr-1.5" />
                                    Active
                                  </>
                                ) : (
                                  <>
                                    <UserX className="w-3.5 h-3.5 mr-1.5" />
                                    Inactive
                                  </>
                                )}
                              </Button>
                            )}

                            {!isSelf && !member.is_active && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteMember(member)}
                                disabled={deleteMember.isPending}
                                className="text-xs border-red-200 text-red-700 hover:bg-red-50"
                              >
                                Delete
                              </Button>
                            )}

                            {canImpersonate && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleImpersonateMember(member)}
                                disabled={impersonateMember.isPending}
                                className="text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
                              >
                                <LogIn className="w-3.5 h-3.5 mr-1.5" />
                                Log in as
                              </Button>
                            )}

                            {isStaff && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setExpandedMembers((prev) => ({ ...prev, [member.id]: !prev[member.id] }))}
                                className="text-xs text-slate-600 hover:text-slate-900"
                              >
                                {isExpanded ? 'Hide pages' : 'Edit pages'}
                              </Button>
                            )}
                          </div>
                        </div>

                        {isStaff && isExpanded && (
                          <div className="pt-1 border-t border-slate-100">
                            <div className="mb-4 rounded-xl border border-slate-200 bg-white p-3">
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                <div>
                                  <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                                    <TabletSmartphone className="w-4 h-4 text-slate-500" />
                                    Shared tablet account
                                  </p>
                                  <p className="text-xs text-slate-500 mt-1">
                                    Employees selected here can use this login from a shared device.
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  disabled={updateMember.isPending || !member.is_active || isSelf}
                                  onClick={() => handleSharedToggle(member, !member.is_shared_account)}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                                    member.is_shared_account
                                      ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                  } disabled:opacity-40`}
                                >
                                  {member.is_shared_account ? 'Shared on' : 'Shared off'}
                                </button>
                              </div>

                              {member.is_shared_account && (
                                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                  {getSharedEmployeeOptions(member).map((employee) => {
                                    const selected = (member.shared_employee_ids || []).includes(employee.id);
                                    return (
                                      <button
                                        key={employee.id}
                                        type="button"
                                        disabled={updateMember.isPending || !member.is_active || isSelf}
                                        onClick={() => handleSharedEmployeeToggle(member, employee.id)}
                                        className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                                          selected
                                            ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                                            : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-white'
                                        } disabled:opacity-40`}
                                      >
                                        <span className={`h-2 w-2 rounded-full ${selected ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                        <span className="min-w-0">
                                          <span className="block truncate font-medium">{employee.name}</span>
                                          <span className="block truncate text-xs opacity-70">{employee.role}</span>
                                        </span>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                                  {t('organization.permissionsTitle')}
                                </p>
                                <p className="text-sm text-slate-500 mt-1">
                                  {t('organization.permissionsSubtitle')}
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={updateMember.isPending || !member.is_active}
                                  onClick={() => handleBulkPermissionChange(member, 'none')}
                                >
                                  Clear all
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={updateMember.isPending || !member.is_active}
                                  onClick={() => handleBulkPermissionChange(member, 'view')}
                                >
                                  All view
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={updateMember.isPending || !member.is_active}
                                  onClick={() => handleBulkPermissionChange(member, 'edit')}
                                >
                                  All edit
                                </Button>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                              {MODULES.map((module) => {
                                const currentLevel = modulePermissions[module.key] || 'none';
                                return (
                                  <div
                                    key={module.key}
                                    className="rounded-xl border border-slate-200 bg-slate-50 p-3"
                                  >
                                    <p className="text-sm font-semibold text-slate-800 mb-2">
                                      {moduleLabel(module.key)}
                                    </p>
                                    <div className="grid grid-cols-3 rounded-lg overflow-hidden border border-slate-200 bg-white">
                                      {LEVELS.map((level) => (
                                        <button
                                          key={level}
                                          type="button"
                                          disabled={updateMember.isPending || !member.is_active || isSelf}
                                          onClick={() => handlePermissionChange(member, module.key, level)}
                                          className={`px-2 py-1.5 text-[11px] font-medium border-r border-slate-100 last:border-r-0 transition-colors ${
                                            currentLevel === level
                                              ? level === 'edit'
                                                ? 'bg-emerald-600 text-white'
                                                : level === 'view'
                                                  ? 'bg-blue-500 text-white'
                                                  : 'bg-slate-600 text-white'
                                              : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
                                          } disabled:opacity-40`}
                                        >
                                          {level === 'none'
                                            ? t('organization.permissionNone')
                                            : level === 'view'
                                              ? t('organization.permissionView')
                                              : t('organization.permissionEdit')}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-800 mb-1">{t('orgPanel.inviteTitle')}</h2>
          <p className="text-sm text-slate-500 mb-4">{t('orgPanel.inviteSubtitle')}</p>

          <Card className="border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <form onSubmit={handleCreateInvite} className="flex flex-col sm:flex-row gap-3">
                {restaurants.length > 1 && (
                  <div className="relative">
                    <select
                      value={inviteRestaurantId}
                      onChange={(e) => setInviteRestaurantId(e.target.value)}
                      className="appearance-none bg-white border border-slate-200 rounded-lg px-3 py-2 pr-8 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {restaurants.map((r) => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                )}
                <div className="relative">
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="appearance-none bg-white border border-slate-200 rounded-lg px-3 py-2 pr-8 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="staff">{t('orgPanel.roleStaff')}</option>
                    <option value="restaurant_admin">{t('orgPanel.roleRestaurantAdmin')}</option>
                    <option value="org_admin">{t('orgPanel.roleOrgAdmin')}</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder={t('orgPanel.inviteEmailPlaceholder')}
                  className="flex-1"
                />
                <Button type="submit" disabled={createInvite.isPending}>
                  <UserPlus className="w-4 h-4 mr-1.5" />
                  {createInvite.isPending ? '...' : t('orgPanel.generateInvite')}
                </Button>
              </form>

              <p className="mt-3 text-xs text-slate-500">
                Add an email to send the invite automatically, or leave it blank to generate a link only.
              </p>

              {inviteNotice && (
                <div className={`mt-4 rounded-xl border p-4 ${inviteNotice.sent ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${inviteNotice.sent ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                      <MailCheck className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-semibold ${inviteNotice.sent ? 'text-emerald-800' : 'text-slate-800'}`}>
                        {inviteNotice.sent && inviteNotice.recipient
                          ? `Invitation email sent to ${inviteNotice.recipient}`
                          : 'Invite link generated'}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {inviteNotice.sent
                          ? 'The email was delivered with a button link. You can still copy the invite URL below if needed.'
                          : 'Share the invite link below with the new team member.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {inviteLink && (
                <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <a
                    href={inviteLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-800 transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Open invite link
                  </a>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyLink}
                    className="flex-shrink-0"
                  >
                    {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                    <span className="ml-1">{copied ? 'Copied' : 'Copy link'}</span>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
